import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Skeleton, Empty } from 'antd';
import ResponsiveBreadcrumb from '../../../components/breadcrumb/ResponsiveBreadcrumb';
import * as AntDIcons from '@ant-design/icons';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import { getCourses } from '../../../services/MicrocredentialsCourseTopicService';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import '../../../pages/common/BraineeModule.css';

const ALLOWED_ROLES = ['super admin', 'admin', 'teacher', 'co-teacher'];

export default function PerformanceTaskCourses({ mode = detectMicrocredentialsTaskMode(window.location?.pathname) } = {}) {
   const navigate = useNavigate();
   const moduleConfig = getMicrocredentialsTaskModule(mode);

   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const [courseList, setCourseList] = useState([]);
   const [loading, setLoading] = useState(false);
   const userId = localStorage.getItem('selected-teacher') ?? Utils.getCurrentUserInfo(Enums.UserInfo.id);

   useEffect(() => {
      Utils.verifyJwt();

      if (!ALLOWED_ROLES.includes(String(userRole || '').toLowerCase())) {
         navigate('/home');
         return;
      }

      setLoading(true);
      getCourses().then((data) => {
         const normalized = Array.isArray(data) ? data : [];
         normalized.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
         setCourseList(normalized);
      }).catch(() => {
         setCourseList([]);
      }).finally(() => {
         setLoading(false);
      });
   }, [navigate, userRole]);

   const courseCountLabel = useMemo(() => (
      loading ? 'Loading courses' : `${courseList.length} courses`
   ), [courseList.length, loading]);

   const handleCourseClick = (course) => {
      const courseDbId = course.course_id;
      const courseKey = course.course_id;
      navigate(`${moduleConfig.teacherCourseRoute}?id=${courseDbId}&courseKey=${encodeURIComponent(courseKey || '')}&name=${encodeURIComponent(course.name || '')}&staff=${userId}`);
   };

   const renderCourseSkeletons = () => (
      <div className="brainee-module-skeleton-grid">
         {Array.from({ length: 6 }).map((_, index) => (
            <div className="brainee-module-card is-skeleton" key={`performance-task-course-skeleton-${index}`}>
               <Skeleton.Avatar active shape="square" className="brainee-module-card-skeleton-icon" />
               <div className="brainee-module-card-copy">
                  <Skeleton.Input active size="small" className="brainee-module-card-skeleton-title" />
                  <Skeleton.Input active size="small" className="brainee-module-card-skeleton-subtitle" />
               </div>
               <Skeleton.Button active size="small" shape="circle" className="brainee-module-card-skeleton-action" />
            </div>
         ))}
      </div>
   );

   return (
      <div className="dashboard-area-all brainee-module-page">
         <div className="dashboard-item brainee-module-shell">
            <div className="brainee-module-breadcrumb">
               <ResponsiveBreadcrumb
                  items={[
                     { key: 'home', node: <Link to="/home">Home</Link> },
                     { key: 'microcredentials', node: 'Brainee Microcredentials' },
                     { key: 'tasks', node: moduleConfig.labelPlural },
                  ]}
               />
            </div>

            <section className="brainee-module-hero">
               <div>
                  <span className="brainee-module-kicker">Brainee Microcredentials</span>
                  <h1>{moduleConfig.labelPlural}</h1>
                  <p>Choose a course to manage {moduleConfig.labelPlural.toLowerCase()}, review records, and organize task work by course.</p>
                  <div className="brainee-module-meta">
                     <span><AntDIcons.BookOutlined /> {courseCountLabel}</span>
                     <span><AntDIcons.FileTextOutlined /> {moduleConfig.label} workspace</span>
                  </div>
               </div>
            </section>

            <section className="brainee-module-panel">
               <div className="brainee-module-panel-header">
                  <div>
                     <span className="brainee-module-kicker">Browse</span>
                     <h2>Course Groups</h2>
                  </div>
                  <span className="brainee-module-count">{loading ? '...' : courseList.length}</span>
               </div>

               {loading
                  ? renderCourseSkeletons()
                  : courseList.length > 0
                     ? (
                        <div className="brainee-module-grid">
                           {courseList.map((course) => {
                              const courseId = course.id ?? course.course_id;
                              const courseTitle = course.name || `Course ${courseId}`;

                              return (
                                 <button className="brainee-module-card" key={courseId} type="button" onClick={() => handleCourseClick(course)}>
                                    <span className="brainee-module-card-icon"><AntDIcons.BookOutlined /></span>
                                    <span className="brainee-module-card-copy">
                                       <span className="brainee-module-card-title">{courseTitle}</span>
                                       <span className="brainee-module-card-subtitle">Open {moduleConfig.labelPlural.toLowerCase()}</span>
                                    </span>
                                    <AntDIcons.RightOutlined className="brainee-module-card-arrow" />
                                 </button>
                              );
                           })}
                        </div>
                     )
                     : (
                        <div className="brainee-module-state">
                           <Empty image="../images/brainee_mascot.png" description="No records found." />
                        </div>
                     )}
            </section>
         </div>
      </div>
   );
}
